import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LogOut, Mail, Plus, Pencil, Trash2, FileText, Inbox, BarChart3, MessageSquare } from "lucide-react";
import { BLOG_CATEGORIES, LOGO_URL } from "../lib/constants";

const STATUS_COLORS = {
  new: "bg-[#1e6dd8]/15 text-[#1e6dd8]",
  contacted: "bg-[#d4af37]/20 text-[#a37e0e]",
  qualified: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
};

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed"];
const BLOG_CATEGORY_OPTIONS = BLOG_CATEGORIES.filter((c) => c !== "All");
const BLANK_POST = {
  title: "",
  excerpt: "",
  content: "",
  category: "Financial Planning",
  cover_image: "",
  author: "HSA Service Hub Editorial",
  published: true,
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#cbd5e1] rounded-xl p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-[#0a2540] font-serif-hsa">{value}</div>
        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ leads_total: 0, leads_new: 0, contacts_total: 0, posts_total: 0 });
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);

  // Post editor state
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_POST);

  useEffect(() => {
    if (!authLoading && !user) nav("/admin/login");
  }, [authLoading, user, nav]);

  const refresh = useCallback(async () => {
    const [s, l, c, p] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/leads"),
      api.get("/admin/contacts"),
      api.get("/admin/blog/posts"),
    ]);
    setStats(s.data); setLeads(l.data); setContacts(c.data); setPosts(p.data);
  }, []);

  useEffect(() => { if (user) refresh().catch(() => {}); }, [user, refresh]);

  const updateLeadStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success("Lead status updated");
    refresh();
  };
  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    refresh();
  };

  const openCreate = () => { setEditing(null); setForm(BLANK_POST); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...BLANK_POST, ...p }); setOpen(true); };
  const submitPost = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/blog/posts/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog/posts", form);
        toast.success("Post published");
      }
      setOpen(false); refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    }
  };
  const deletePost = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/posts/${id}`);
    refresh();
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <header className="bg-white border-b border-[#cbd5e1] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="HSA" className="h-10 w-10 object-cover rounded-md" />
            <div>
              <div className="font-serif-hsa text-base font-bold text-[#0a2540]">HSA Admin</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Service Hub Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline">{user.email}</span>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm"
              onClick={async () => { await logout(); nav("/admin/login"); }}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <h1 className="font-serif-hsa text-3xl font-bold text-[#0a2540] mb-2">Dashboard</h1>
        <p className="text-slate-500 mb-8">Manage consultation leads, contact submissions, and blog content.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total Leads" value={stats.leads_total} accent="bg-[#1e6dd8]/15 text-[#1e6dd8]" />
          <StatCard icon={BarChart3} label="New Leads" value={stats.leads_new} accent="bg-[#d4af37]/20 text-[#a37e0e]" />
          <StatCard icon={MessageSquare} label="Contact Forms" value={stats.contacts_total} accent="bg-emerald-100 text-emerald-700" />
          <StatCard icon={FileText} label="Blog Posts" value={stats.posts_total} accent="bg-slate-200 text-slate-700" />
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-white border border-[#cbd5e1]">
            <TabsTrigger data-testid="tab-leads" value="leads">Consultation Leads</TabsTrigger>
            <TabsTrigger data-testid="tab-contacts" value="contacts">Contact Forms</TabsTrigger>
            <TabsTrigger data-testid="tab-blog" value="blog">Blog Posts</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="bg-white border border-[#cbd5e1] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fa] text-left">
                    <tr className="text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Contact</th>
                      <th className="px-5 py-3">Service</th>
                      <th className="px-5 py-3">State</th>
                      <th className="px-5 py-3">Type</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {leads.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No leads yet.</td></tr>
                    )}
                    {leads.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-[#f8f9fa]">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-[#0a2540]">{l.full_name}</div>
                          {l.message && <div className="text-xs text-slate-500 mt-1 max-w-xs truncate">{l.message}</div>}
                        </td>
                        <td className="px-5 py-4 text-slate-700">
                          <div>{l.email}</div>
                          <div className="text-xs text-slate-500">{l.phone}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-700">{l.service}</td>
                        <td className="px-5 py-4 text-slate-700">{l.state}</td>
                        <td className="px-5 py-4 text-slate-700">{l.client_type}</td>
                        <td className="px-5 py-4">
                          <Select value={l.status} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                            <SelectTrigger data-testid={`lead-status-select-${l.id}`} className="h-8 w-32">
                              <SelectValue><Badge className={`${STATUS_COLORS[l.status] || ""} font-normal`}>{l.status}</Badge></SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4">
                          <Button data-testid={`lead-delete-${l.id}`} variant="ghost" size="sm" onClick={() => deleteLead(l.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="mt-6">
            <div className="bg-white border border-[#cbd5e1] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fa] text-left">
                    <tr className="text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">From</th>
                      <th className="px-5 py-3">Subject</th>
                      <th className="px-5 py-3">Message</th>
                      <th className="px-5 py-3">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {contacts.length === 0 && (
                      <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No contact form submissions yet.</td></tr>
                    )}
                    {contacts.map((c) => (
                      <tr key={c.id}>
                        <td className="px-5 py-4">
                          <div className="font-semibold text-[#0a2540]">{c.full_name}</div>
                          <div className="text-xs text-slate-500">{c.email}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-700">{c.subject || "—"}</td>
                        <td className="px-5 py-4 text-slate-600 max-w-md">{c.message}</td>
                        <td className="px-5 py-4 text-slate-500 text-xs">
                          {new Date(c.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif-hsa text-xl font-semibold text-[#0a2540]">Articles</h3>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-post-btn" onClick={openCreate} className="bg-[#0a2540] hover:bg-[#1e6dd8] text-white">
                    <Plus className="w-4 h-4 mr-2" /> New Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif-hsa text-2xl text-[#0a2540]">
                      {editing ? "Edit Article" : "New Article"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitPost} className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input data-testid="post-input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="post-select-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {BLOG_CATEGORY_OPTIONS.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cover Image URL</Label>
                      <Input data-testid="post-input-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..." />
                    </div>
                    <div>
                      <Label>Excerpt</Label>
                      <Textarea data-testid="post-input-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <Textarea data-testid="post-input-content" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
                    </div>
                    <div className="flex items-center gap-2">
                      <input id="published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                      <Label htmlFor="published">Published (visible on site)</Label>
                    </div>
                    <DialogFooter>
                      <Button data-testid="post-save-btn" type="submit" className="bg-[#0a2540] hover:bg-[#1e6dd8] text-white">
                        {editing ? "Update Article" : "Publish Article"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {posts.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500 bg-white border border-[#cbd5e1] rounded-xl">No articles yet.</div>
              )}
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-white border border-[#cbd5e1] rounded-xl p-5">
                  <div className="flex justify-between items-start mb-2">
                    <Badge className="bg-[#1e6dd8]/15 text-[#1e6dd8] font-normal">{p.category}</Badge>
                    {!p.published && <Badge variant="outline" className="text-xs">Draft</Badge>}
                  </div>
                  <h4 className="font-serif-hsa text-lg font-semibold text-[#0a2540] mb-2 leading-snug">{p.title}</h4>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{p.excerpt}</p>
                  <div className="flex gap-2">
                    <Button data-testid={`post-edit-${p.id}`} variant="outline" size="sm" onClick={() => openEdit(p)}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button data-testid={`post-delete-${p.id}`} variant="ghost" size="sm" onClick={() => deletePost(p.id)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1.5 text-red-500" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
